import React, { useContext, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AppContext } from '@state/context'
import { openPanel, openPanelWithData } from '@state/actions/panelsActions'
import {
  PANELS,
  BUTTON_SIZE,
  BUTTON_TYPE,
  CONTAINER_STYLE,
  HEADING_TYPE,
  TEXT_SIZES,
  DIVIDER_SIZE,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import SubHeader from '@views/SubHeader'
import Container from '@components/Container'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Text from '@components/Text'
import Loader from '@components/Loader'
import Button from '@components/Button'
import SelectButton from '@components/SelectButton'
import GridColumns from '@components/GridColumns'
import StudentList from '@components/StudentList'
import SharedBooksList from '@components/SharedBooksList'
import { useSchoolClass } from '@hooks/useSchoolClass'
import { useSchool } from '@hooks/useSchool'
import SchoolResourceList from '@components/SchoolResourceList'
import { useUpdateSchoolClass } from '@hooks/mutations/useUpdateSchoolClass'
import { useSchoolClassesOptions } from '@hooks/useSchoolClassesOptions'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useBooksWithRelatedSchoolGroups } from '@hooks/useBooksWithRelatedSchoolGroups'
import { useConfirmRemoveSchoolClass } from '@hooks/useConfirmRemoveSchoolClass'

const SingleSchoolClass = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [, dispatch] = useContext(AppContext)
  const { booksWithRelatedSchoolGroups, relatedSchoolGroups } =
    useBooksWithRelatedSchoolGroups(id)
  const { currentUser } = useCurrentUser()
  const { schoolClass, error } = useSchoolClass(id)
  const { school } = useSchool(schoolClass?.school?.id)
  const { schoolClassesOptions } = useSchoolClassesOptions()
  const { confirmRemoveSchoolClass } = useConfirmRemoveSchoolClass()
  const breadcrumb = useMemo(
    () =>
      schoolClass && {
        label: schoolClass?.name,
      },
    [schoolClass]
  )

  return (
    <>
      <SubHeader breadcrumb={breadcrumb}>
        <SubHeader.MiddleSection />
        <SubHeader.RightSection>
          {schoolClass && schoolClassesOptions?.length > 0 && (
            <SelectButton
              onChange={(e) => {
                navigate(e.target.value, {
                  state: { replaceLastBreadcrumb: true },
                })
              }}
              name={'schoolClass'}
              id={'schoolClass'}
              options={schoolClassesOptions}
              defaultValue={`/klass/${schoolClass.id}`}
              extraClasses={'max-w-[200px]'}
            />
          )}
        </SubHeader.RightSection>
      </SubHeader>

      {!schoolClass && !error && <Loader />}
      {error && <Text>Någonting gick fel när vi skulle hämta klass</Text>}

      {schoolClass && !error && (
        <div
          className={'flex flex-col'}
          style={{ minHeight: 'calc(100vh - 146px)' }}
        >
          <Container size={CONTAINER_STYLE.DESKTOP}>
            <div className={'flex justify-center'}>
              <Heading headingLevel={HEADING_TYPE.H1} size={TEXT_SIZES.XXLARGE}>
                {schoolClass?.name}
              </Heading>
            </div>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <div className={'flex gap-2 align-middle'}>
              {!currentUser?.hasPaidLicense && school && schoolClass && (
                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.MEDIUM}
                  icon={'PlusCircle'}
                  iconLeft
                  margin
                  handleClick={() => {
                    dispatch(
                      openPanelWithData({
                        panel: PANELS.REGISTER_STUDENTS,
                        data: { school, schoolClass },
                      })
                    )
                  }}
                >
                  Skriv in elever
                </Button>
              )}

              {schoolClass?.isPrivate &&
                currentUser?.hasValidPubAgreement &&
                booksWithRelatedSchoolGroups?.length > 0 && (
                  <Button
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.MEDIUM}
                    icon={'PlusCircle'}
                    iconLeft
                    handleClick={() => {
                      dispatch(openPanel(PANELS.MIGRATE_MY_SCHOOL_CLASS))
                    }}
                  >
                    Flytta böcker till ny klass
                  </Button>
                )}

              {schoolClass?.isPrivate &&
                currentUser?.hasValidPubAgreement &&
                currentUser?.hasPaidLicense && (
                  <Button
                    type={BUTTON_TYPE.ALERT_RED_DARK}
                    size={BUTTON_SIZE.MEDIUM}
                    handleClick={
                      currentUser?.hasValidPubAgreement
                        ? () =>
                            dispatch(openPanel(PANELS.DELETE_MY_SCHOOL_CLASS))
                        : confirmRemoveSchoolClass
                    }
                  >
                    Radera
                  </Button>
                )}
            </div>
          </Container>

          <Divider line size={DIVIDER_SIZE.SMALL} />

          <div className={'xl:container xl:mx-auto px-10 flex-auto h-[1px]'}>
            <GridColumns cols={4} classes={'h-full gap-7'}>
              <div className={'h-full border-r border-mediumGrey'}>
                <Divider size={DIVIDER_SIZE.BASE} />
                <StudentList
                  items={schoolClass.students}
                  resourceType={SCHOOL_RESOURCE_TYPES.CLASS}
                  resourceId={schoolClass.id}
                  updateHook={useUpdateSchoolClass}
                />
                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
              <div className={'col-span-3'}>
                <Divider size={DIVIDER_SIZE.BASE} />

                {booksWithRelatedSchoolGroups && (
                  <SharedBooksList
                    // items={booksWithRelatedSchoolGroups} // TODO, to show group-books aswell
                    items={schoolClass?.books}
                    resourceType={SCHOOL_RESOURCE_TYPES.CLASS}
                    resource={schoolClass}
                  />
                )}

                <Divider size={DIVIDER_SIZE.BASE} />

                {relatedSchoolGroups?.length > 0 && (
                  <>
                    <Heading
                      headingLevel={HEADING_TYPE.H2}
                      size={TEXT_SIZES.XLARGE}
                    >
                      {`Klassens grupper`}
                    </Heading>

                    <Divider size={DIVIDER_SIZE.SMALL} />

                    <SchoolResourceList
                      items={relatedSchoolGroups}
                      gridCols={2}
                      resourceType={SCHOOL_RESOURCE_TYPES.GROUP}
                    />

                    <Divider size={DIVIDER_SIZE.BASE} />
                  </>
                )}
              </div>
            </GridColumns>
          </div>
        </div>
      )}
    </>
  )
}

export default SingleSchoolClass
